import {
  ComparisonSpecType,
  dateISOStringWithoutTimezone,
  DateTrunc,
  DateWindow,
  DateWindowTypes,
  DefinedStartDateWindow,
  isHistoricalComparisonSpec,
  isHistoricalOrMarkBasedComparison,
  isStaticComparison,
  LastNDateWindow,
  LatestDataDateWindow,
  MetricConfig,
  NullableStatus,
  shouldShowComparisonLine,
  ThresholdStatus as EngineThresholdStatus,
} from '@tableau/metrics-engine';
import { DateTime } from '@tableau/metrics-models-typescript-interface/lib/tableau/metrics/models/data_values_pb';
import { DateGranularity } from '@tableau/metrics-models-typescript-interface/lib/tableau/metrics/models/date_granularity_pb';
import {
  AllTimeWindowType,
  DateWindow as DateWindowProtobuf,
  DefinedStartWindowType,
  LatestDataWindowType,
} from '@tableau/metrics-models-typescript-interface/lib/tableau/metrics/models/date_window_pb';
import { Duration } from '@tableau/metrics-models-typescript-interface/lib/tableau/metrics/models/duration_pb';
import { MetricConfiguration } from '@tableau/metrics-models-typescript-interface/lib/tableau/metrics/models/metric_configuration_pb';
import {
  NumberOfPeriodsThresholdType,
  SimpleStatus,
  StaticValueThresholdType,
  StaticValueThresholdValue,
  Threshold,
  ThresholdStatus,
} from '@tableau/metrics-models-typescript-interface/lib/tableau/metrics/models/threshold_pb';
import { Message } from 'google-protobuf';

/**
 * The "configuration" fields shared between a `MetricDefinition` and a `MetricConfiguration`
 */
export interface ConfigurationFields {
  thresholdsList: Threshold[];
  dateWindowList: DateWindowProtobuf[];
}

export function protoToB64(proto: Message): string {
  return Message.bytesAsB64(proto.serializeBinary());
}

/**
 *
 * @param config the `MetricConfig` to convert
 * @returns a `MetricConfiguration` protobuf
 */
export function configToProto(config: MetricConfig): MetricConfiguration {
  const fields = configToProtoFields(config);
  const configProto = new MetricConfiguration();
  configProto.setThresholdsList(fields.thresholdsList);
  configProto.setDateWindowList(fields.dateWindowList);
  return configProto;
}

/**
 *
 * @param config the `MetricConfig` to convert
 * @returns a `ConfigurationFields` matching the given config.
 */
export function configToProtoFields({ comparison, status, window }: MetricConfig): ConfigurationFields {
  const thresholdsList = makeThresholdsList({ selectedComparison: comparison, selectedStatus: status });
  const dateWindowList = makeDateWindowList(window);
  return { thresholdsList, dateWindowList };
}

function makeDateWindowList(selectedDateWindow: DateWindow | null): DateWindowProtobuf[] {
  let windowList: DateWindowProtobuf[] = [];
  if (selectedDateWindow !== null) {
    windowList = [dateWindowToProtobuf(selectedDateWindow)];
  }
  return windowList;
}

function makeThresholdsList({
  selectedComparison,
  selectedStatus,
}: {
  selectedComparison: ComparisonSpecType | null;
  selectedStatus: NullableStatus;
}): Threshold[] {
  if (selectedComparison === null) {
    return [];
  }
  if (isStaticComparison(selectedComparison)) {
    const thresholdValue = new StaticValueThresholdValue();
    thresholdValue.setDoubleValue(selectedComparison.value);

    const thresholdType = new StaticValueThresholdType();
    thresholdType.setValue(thresholdValue);

    const status = statusToProto(selectedStatus);
    if (status !== undefined) {
      thresholdType.setStatus(status);
    }

    const staticThreshold = new Threshold();
    staticThreshold.setTypeStaticValue(thresholdType);
    return [staticThreshold];
  }

  if (isHistoricalOrMarkBasedComparison(selectedComparison)) {
    const thresholdValue = new NumberOfPeriodsThresholdType();
    thresholdValue.setShowComparisonLine(shouldShowComparisonLine(selectedComparison));

    const duration = new Duration();
    const numberOfUnits = isHistoricalComparisonSpec(selectedComparison)
      ? selectedComparison.numberOfPeriods ?? 1
      : selectedComparison.numberOfMarks;
    duration.setNumberOfUnits(numberOfUnits);
    const dateTrunc = isHistoricalComparisonSpec(selectedComparison)
      ? selectedComparison.comparisonPeriod
      : DateTrunc.UNKNOWN;

    duration.setDateGranularity(dateTruncToProtobuf(dateTrunc));

    thresholdValue.setDuration(duration);

    const numberOfPeriodsThreshold = new Threshold();
    numberOfPeriodsThreshold.setTypeNumberOfPeriods(thresholdValue);
    return [numberOfPeriodsThreshold];
  }
  return [];
}

function statusToProto(selectedStatus: NullableStatus): SimpleStatus | undefined {
  let status: SimpleStatus | undefined = undefined;
  if (selectedStatus.above !== null || selectedStatus.equal !== null || selectedStatus.below !== null) {
    status = new SimpleStatus();
    status.setAbove(thresholdStatusToProtobuf(unspecifiedStatusIfNull(selectedStatus.above)));
    status.setEqual(thresholdStatusToProtobuf(unspecifiedStatusIfNull(selectedStatus.equal)));
    status.setBelow(thresholdStatusToProtobuf(unspecifiedStatusIfNull(selectedStatus.below)));
  }
  return status;
}

const thresholdStatusToProtobuf = (thresholdStatus: EngineThresholdStatus) => {
  switch (thresholdStatus) {
    case EngineThresholdStatus.BAD:
      return ThresholdStatus.THRESHOLD_STATUS_BAD;
    case EngineThresholdStatus.GOOD:
      return ThresholdStatus.THRESHOLD_STATUS_GOOD;
    case EngineThresholdStatus.NEUTRAL:
      return ThresholdStatus.THRESHOLD_STATUS_NEUTRAL;
    case EngineThresholdStatus.UNKNOWN:
    default:
      return ThresholdStatus.THRESHOLD_STATUS_UNSPECIFIED;
  }
};

const unspecifiedStatusIfNull = (thresholdStatus: EngineThresholdStatus | null) => {
  return thresholdStatus === null ? EngineThresholdStatus.UNKNOWN : thresholdStatus;
};

type DateGranularityProto = typeof DateGranularity[keyof typeof DateGranularity];

const dateTruncToProtobuf = (dateTrunc?: DateTrunc): DateGranularityProto => {
  switch (dateTrunc) {
    case DateTrunc.SECOND: {
      return DateGranularity.DATE_GRANULARITY_SECOND;
    }
    case DateTrunc.MINUTE: {
      return DateGranularity.DATE_GRANULARITY_MINUTE;
    }
    case DateTrunc.HOUR: {
      return DateGranularity.DATE_GRANULARITY_HOUR;
    }
    case DateTrunc.DAY: {
      return DateGranularity.DATE_GRANULARITY_DAY;
    }
    case DateTrunc.WEEK: {
      return DateGranularity.DATE_GRANULARITY_WEEK;
    }
    case DateTrunc.MONTH: {
      return DateGranularity.DATE_GRANULARITY_MONTH;
    }
    case DateTrunc.QUARTER: {
      return DateGranularity.DATE_GRANULARITY_QUARTER;
    }
    case DateTrunc.YEAR: {
      return DateGranularity.DATE_GRANULARITY_YEAR;
    }
    case DateTrunc.UNKNOWN:
    case DateTrunc.NONE:
    case undefined:
    default: {
      return DateGranularity.DATE_GRANULARITY_UNKNOWN;
    }
  }
};

const dateWindowToProtobuf = (dateWindow: DateWindow): DateWindowProtobuf => {
  const dateWindowProtobuf = new DateWindowProtobuf();
  switch (dateWindow.type) {
    case DateWindowTypes.LAST_N: {
      const lastNWindow = dateWindow as LastNDateWindow;
      const latestWindowProtobuf = new LatestDataWindowType();

      const duration = new Duration();
      duration.setNumberOfUnits(lastNWindow.n);
      duration.setDateGranularity(dateTruncToProtobuf(DateTrunc.UNKNOWN));

      latestWindowProtobuf.setDuration(duration);
      dateWindowProtobuf.setTypeLatestDataWindow(latestWindowProtobuf);
      break;
    }
    case DateWindowTypes.LATEST_DATA: {
      const latestWindow = dateWindow as LatestDataDateWindow;
      const latestWindowProtobuf = new LatestDataWindowType();

      const duration = new Duration();
      duration.setNumberOfUnits(latestWindow.duration.numberOfUnits);
      duration.setDateGranularity(dateTruncToProtobuf(latestWindow.duration.unit));

      latestWindowProtobuf.setDuration(duration);
      dateWindowProtobuf.setTypeLatestDataWindow(latestWindowProtobuf);
      break;
    }
    case DateWindowTypes.DEFINED_START: {
      const definedStart = dateWindow as DefinedStartDateWindow;
      const definedStartProtobuf = new DefinedStartWindowType();

      const dateTime = new DateTime();
      dateTime.setDateTime(dateISOStringWithoutTimezone(definedStart.startDate));

      definedStartProtobuf.setStartDate(dateTime);
      dateWindowProtobuf.setTypeDefinedStartWindow(definedStartProtobuf);
      break;
    }
    case DateWindowTypes.SPECIAL: {
      dateWindowProtobuf.setTypeAllTimeWindow(new AllTimeWindowType());
      break;
    }
    case DateWindowTypes.CUSTOM:
    case undefined:
    default:
  }
  return dateWindowProtobuf;
};
